import React, { useMemo, useState } from 'react';
import jsPDF from 'jspdf';
import {
  FaDownload,
  FaPrint,
  FaRedo,
  FaUtensils,
  FaPlus,
  FaTrash,
} from 'react-icons/fa';
import SEO from '../components/SEO';
import LogoUploader from '../components/LogoUploader';
import { drawPdfLogo } from '../utils/logoUpload';

const PAYMENT_MODES = ['Cash', 'Card', 'UPI', 'Wallet', 'Net Banking'];

const TEMPLATES = [
  { id: 'classic', label: 'Classic Thermal', accent: '#0f766e', format: [80, 297] },
  { id: 'banner', label: 'Bold Banner', accent: '#dc2626', format: [80, 297] },
  { id: 'minimal', label: 'Minimal Mono', accent: '#111827', format: [80, 297] },
  { id: 'sidebar', label: 'Side Strip', accent: '#7c3aed', format: [80, 297] },
  { id: 'invoice', label: 'A4 GST Invoice', accent: '#1d4ed8', format: 'a4' },
];

const hexToRgbArr = (hex) => {
  const h = hex.replace('#', '');
  return [
    parseInt(h.substring(0, 2), 16),
    parseInt(h.substring(2, 4), 16),
    parseInt(h.substring(4, 6), 16),
  ];
};

const pad2 = (n) => String(n).padStart(2, '0');
const todayDMY = () => {
  const d = new Date();
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
};
const nowHM = () => {
  const d = new Date();
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};
const generateBillNumber = () => {
  const year = new Date().getFullYear();
  const seq = Math.floor(1000 + Math.random() * 9000);
  return `INV-${year}-${seq}`;
};

const formatINR = (n) =>
  Number(n || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const newItem = (name = '', qty = 1, price = 0) => ({
  id: Math.random().toString(36).slice(2, 9),
  name,
  qty,
  price,
});

const RestaurantBillGenerator = () => {
  const [template, setTemplate] = useState('classic');

  const [restaurant, setRestaurant] = useState({
    name: 'Spice Garden Restaurant',
    address: '24 MG Road, Bengaluru 560001',
    phone: '+91 98765 43210',
    gstin: '29ABCDE1234F1Z5',
    fssai: '10012345001234',
  });

  const [meta, setMeta] = useState({
    billNo: generateBillNumber(),
    date: todayDMY(),
    time: nowHM(),
    tableNo: '7',
    server: 'Ravi',
    customer: '',
    paymentMode: 'UPI',
  });

  const [items, setItems] = useState([
    newItem('Paneer Butter Masala', 1, 280),
    newItem('Garlic Naan', 4, 60),
    newItem('Jeera Rice', 1, 180),
    newItem('Masala Chai', 2, 40),
  ]);

  const [tax, setTax] = useState({
    cgst: 2.5,
    sgst: 2.5,
    serviceCharge: 5,
    discountPercent: 0,
  });

  const [logo, setLogo] = useState(null);

  const accent = TEMPLATES.find((t) => t.id === template)?.accent || '#0f766e';

  // ---------- COMPUTATIONS ----------
  const totals = useMemo(() => {
    const subtotal = items.reduce(
      (s, it) => s + (Number(it.qty) || 0) * (Number(it.price) || 0),
      0
    );
    const discount = (subtotal * (Number(tax.discountPercent) || 0)) / 100;
    const afterDiscount = subtotal - discount;
    const serviceCharge =
      (afterDiscount * (Number(tax.serviceCharge) || 0)) / 100;
    const taxable = afterDiscount + serviceCharge;
    const cgst = (taxable * (Number(tax.cgst) || 0)) / 100;
    const sgst = (taxable * (Number(tax.sgst) || 0)) / 100;
    const grand = taxable + cgst + sgst;
    return {
      subtotal,
      discount,
      serviceCharge,
      cgst,
      sgst,
      grand: Math.round(grand * 100) / 100,
      grandRounded: Math.round(grand),
      roundOff: Math.round(grand) - grand,
    };
  }, [items, tax]);

  const updateItem = (id, patch) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  };
  const addItem = () => setItems((prev) => [...prev, newItem()]);
  const removeItem = (id) => setItems((prev) => prev.filter((it) => it.id !== id));

  const handleReset = () => {
    setRestaurant({ name: '', address: '', phone: '', gstin: '', fssai: '' });
    setMeta({
      billNo: generateBillNumber(),
      date: todayDMY(),
      time: nowHM(),
      tableNo: '',
      server: '',
      customer: '',
      paymentMode: 'Cash',
    });
    setItems([newItem()]);
    setTax({ cgst: 2.5, sgst: 2.5, serviceCharge: 0, discountPercent: 0 });
  };

  // ---------- PDF RENDERERS ----------
  const drawItemsThermal = (doc, startY, w) => {
    let y = startY;
    const left = 5;
    const right = w - 5;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    doc.text('Item', left, y);
    doc.text('Qty', left + 38, y, { align: 'right' });
    doc.text('Rate', left + 55, y, { align: 'right' });
    doc.text('Amt', right, y, { align: 'right' });
    y += 3;
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.2);
    doc.line(left, y, right, y);
    y += 4;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    items.forEach((it) => {
      const name = it.name || '-';
      const qty = Number(it.qty) || 0;
      const price = Number(it.price) || 0;
      const amt = qty * price;
      const nameLines = doc.splitTextToSize(name, 35);
      doc.text(nameLines, left, y);
      doc.text(String(qty), left + 38, y, { align: 'right' });
      doc.text(price.toFixed(2), left + 55, y, { align: 'right' });
      doc.text(amt.toFixed(2), right, y, { align: 'right' });
      y += Math.max(4, nameLines.length * 4);
    });
    y += 2;
    doc.line(left, y, right, y);
    return y + 4;
  };

  const drawTotalsThermal = (doc, startY, w, color) => {
    let y = startY;
    const left = 5;
    const right = w - 5;
    const row = (label, value, bold = false) => {
      doc.setFont('helvetica', bold ? 'bold' : 'normal');
      doc.setFontSize(bold ? 11 : 9);
      doc.setTextColor(0, 0, 0);
      doc.text(label, left, y);
      doc.text(value, right, y, { align: 'right' });
      y += bold ? 6 : 5;
    };
    row('Subtotal', formatINR(totals.subtotal));
    if (totals.discount > 0)
      row(`Discount (${tax.discountPercent}%)`, `- ${formatINR(totals.discount)}`);
    if (totals.serviceCharge > 0)
      row(`Service (${tax.serviceCharge}%)`, formatINR(totals.serviceCharge));
    if (Number(tax.cgst) > 0) row(`CGST (${tax.cgst}%)`, formatINR(totals.cgst));
    if (Number(tax.sgst) > 0) row(`SGST (${tax.sgst}%)`, formatINR(totals.sgst));
    y += 1;
    doc.setDrawColor(...color);
    doc.setLineWidth(0.4);
    doc.line(left, y, right, y);
    y += 5;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(...color);
    doc.text('GRAND TOTAL', left, y);
    doc.text(`Rs. ${formatINR(totals.grand)}`, right, y, { align: 'right' });
    doc.setTextColor(0, 0, 0);
    y += 6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(`Payment: ${meta.paymentMode}`, left, y);
    y += 5;
    return y;
  };

  // 1. CLASSIC THERMAL
  const renderClassic = () => {
    const w = 80;
    const h = 297;
    const doc = new jsPDF({ unit: 'mm', format: [w, h] });
    const color = hexToRgbArr(accent);
    let y = 10;

    if (logo) {
      drawPdfLogo(doc, logo, (w - 16) / 2, 4, 16, restaurant.name, [240, 240, 240]);
      y = 24;
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(...color);
    doc.text(restaurant.name || 'Restaurant', w / 2, y, { align: 'center' });
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(0, 0, 0);
    if (restaurant.address) {
      const lines = doc.splitTextToSize(restaurant.address, w - 10);
      lines.forEach((ln) => { doc.text(ln, w / 2, y, { align: 'center' }); y += 3.5; });
    }
    if (restaurant.phone) { doc.text(`Tel: ${restaurant.phone}`, w / 2, y, { align: 'center' }); y += 3.5; }
    if (restaurant.gstin) { doc.text(`GSTIN: ${restaurant.gstin}`, w / 2, y, { align: 'center' }); y += 3.5; }
    if (restaurant.fssai) { doc.text(`FSSAI: ${restaurant.fssai}`, w / 2, y, { align: 'center' }); y += 3.5; }

    y += 2;
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.3);
    doc.line(5, y, w - 5, y);
    y += 5;

    doc.setFontSize(9);
    doc.text(`Bill: ${meta.billNo}`, 5, y);
    doc.text(meta.date, w - 5, y, { align: 'right' });
    y += 4;
    doc.text(`Table: ${meta.tableNo || '-'}`, 5, y);
    doc.text(meta.time, w - 5, y, { align: 'right' });
    y += 4;
    if (meta.server) { doc.text(`Server: ${meta.server}`, 5, y); y += 4; }
    if (meta.customer) { doc.text(`Cust: ${meta.customer}`, 5, y); y += 4; }
    y += 2;

    y = drawItemsThermal(doc, y, w);
    y = drawTotalsThermal(doc, y, w, color);

    y += 4;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...color);
    doc.text('Thank you! Visit again.', w / 2, y, { align: 'center' });
    return doc;
  };

  // 2. BOLD BANNER
  const renderBanner = () => {
    const w = 80;
    const h = 297;
    const doc = new jsPDF({ unit: 'mm', format: [w, h] });
    const color = hexToRgbArr(accent);
    let y = 0;

    const bannerH = logo ? 36 : 24;
    doc.setFillColor(...color);
    doc.rect(0, 0, w, bannerH, 'F');
    if (logo) drawPdfLogo(doc, logo, (w - 14) / 2, 3, 14, restaurant.name, [255, 255, 255]);
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text(restaurant.name || 'Restaurant', w / 2, logo ? 22 : 10, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text('TAX INVOICE', w / 2, logo ? 28 : 16, { align: 'center' });
    if (restaurant.gstin) doc.text(`GSTIN ${restaurant.gstin}`, w / 2, logo ? 32 : 20, { align: 'center' });

    y = bannerH + 4;
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(8);
    if (restaurant.address) {
      const lines = doc.splitTextToSize(restaurant.address, w - 10);
      lines.forEach((ln) => { doc.text(ln, w / 2, y, { align: 'center' }); y += 3.5; });
    }
    if (restaurant.phone) { doc.text(`Tel: ${restaurant.phone}`, w / 2, y, { align: 'center' }); y += 3.5; }
    y += 2;

    doc.setFontSize(9);
    doc.text(`Bill: ${meta.billNo}`, 5, y);
    doc.text(`${meta.date} ${meta.time}`, w - 5, y, { align: 'right' });
    y += 4;
    doc.text(`Table ${meta.tableNo || '-'}${meta.server ? ` | ${meta.server}` : ''}`, 5, y);
    y += 5;

    y = drawItemsThermal(doc, y, w);

    // Big total banner
    const subTotalsY = y;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    const tline = (label, value) => {
      doc.text(label, 5, y);
      doc.text(value, w - 5, y, { align: 'right' });
      y += 4;
    };
    tline('Subtotal', formatINR(totals.subtotal));
    if (totals.discount > 0) tline(`Discount (${tax.discountPercent}%)`, `- ${formatINR(totals.discount)}`);
    if (totals.serviceCharge > 0) tline(`Service (${tax.serviceCharge}%)`, formatINR(totals.serviceCharge));
    if (Number(tax.cgst) > 0) tline(`CGST ${tax.cgst}%`, formatINR(totals.cgst));
    if (Number(tax.sgst) > 0) tline(`SGST ${tax.sgst}%`, formatINR(totals.sgst));
    y += 2;

    doc.setFillColor(...color);
    doc.rect(4, y, w - 8, 12, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('TOTAL', 6, y + 8);
    doc.text(`Rs. ${formatINR(totals.grand)}`, w - 6, y + 8, { align: 'right' });
    y += 16;
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(`Paid via ${meta.paymentMode}`, w / 2, y, { align: 'center' });
    y += 6;
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...color);
    doc.text('Thank you! Visit again.', w / 2, y, { align: 'center' });
    // suppress unused-var warning in some setups
    void subTotalsY;
    return doc;
  };

  // 3. MINIMAL MONO
  const renderMinimal = () => {
    const w = 80;
    const h = 297;
    const doc = new jsPDF({ unit: 'mm', format: [w, h] });
    let y = 12;
    if (logo) {
      drawPdfLogo(doc, logo, (w - 14) / 2, 4, 14, restaurant.name, [240, 240, 240]);
      y = 22;
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(0, 0, 0);
    doc.text(restaurant.name || 'Restaurant', w / 2, y, { align: 'center' });
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    if (restaurant.address) {
      const lines = doc.splitTextToSize(restaurant.address, w - 10);
      lines.forEach((ln) => { doc.text(ln, w / 2, y, { align: 'center' }); y += 3.5; });
    }
    if (restaurant.phone) { doc.text(restaurant.phone, w / 2, y, { align: 'center' }); y += 3.5; }
    y += 2;
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.4);
    doc.line(5, y, w - 5, y);
    y += 5;

    doc.setFontSize(9);
    doc.text(meta.billNo, 5, y);
    doc.text(`${meta.date} ${meta.time}`, w - 5, y, { align: 'right' });
    y += 4;
    doc.text(`Table ${meta.tableNo || '-'}`, 5, y);
    if (meta.server) doc.text(meta.server, w - 5, y, { align: 'right' });
    y += 5;

    y = drawItemsThermal(doc, y, w);

    const row = (label, value, bold = false) => {
      doc.setFont('helvetica', bold ? 'bold' : 'normal');
      doc.setFontSize(bold ? 11 : 9);
      doc.text(label, 5, y);
      doc.text(value, w - 5, y, { align: 'right' });
      y += bold ? 6 : 4.5;
    };
    row('Subtotal', formatINR(totals.subtotal));
    if (totals.discount > 0) row(`Discount`, `- ${formatINR(totals.discount)}`);
    if (totals.serviceCharge > 0) row(`Service`, formatINR(totals.serviceCharge));
    if (Number(tax.cgst) > 0) row(`CGST`, formatINR(totals.cgst));
    if (Number(tax.sgst) > 0) row(`SGST`, formatINR(totals.sgst));
    y += 1;
    doc.line(5, y, w - 5, y);
    y += 5;
    row('TOTAL', `Rs. ${formatINR(totals.grand)}`, true);
    y += 4;
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text(`Paid via ${meta.paymentMode}`, w / 2, y, { align: 'center' });
    y += 5;
    doc.text('Thank you. Please visit again.', w / 2, y, { align: 'center' });
    return doc;
  };

  // 4. SIDE STRIP
  const renderSidebar = () => {
    const w = 80;
    const h = 297;
    const doc = new jsPDF({ unit: 'mm', format: [w, h] });
    const color = hexToRgbArr(accent);
    const stripW = 8;
    doc.setFillColor(...color);
    doc.rect(0, 0, stripW, h, 'F');

    let y = 10;
    if (logo) {
      drawPdfLogo(doc, logo, (w + stripW - 14) / 2, 4, 14, restaurant.name, [240, 240, 240]);
      y = 22;
    }
    const left = stripW + 4;
    const right = w - 4;
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text(restaurant.name || 'Restaurant', (w + stripW) / 2, y, { align: 'center' });
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    if (restaurant.address) {
      const lines = doc.splitTextToSize(restaurant.address, w - stripW - 8);
      lines.forEach((ln) => { doc.text(ln, (w + stripW) / 2, y, { align: 'center' }); y += 3.5; });
    }
    if (restaurant.phone) { doc.text(`Tel: ${restaurant.phone}`, (w + stripW) / 2, y, { align: 'center' }); y += 3.5; }
    if (restaurant.gstin) { doc.text(`GSTIN ${restaurant.gstin}`, (w + stripW) / 2, y, { align: 'center' }); y += 3.5; }
    y += 2;
    doc.setDrawColor(...color);
    doc.setLineWidth(0.3);
    doc.line(left, y, right, y);
    y += 5;

    doc.setFontSize(9);
    doc.text(`Bill: ${meta.billNo}`, left, y);
    doc.text(meta.date, right, y, { align: 'right' });
    y += 4;
    doc.text(`Table: ${meta.tableNo || '-'}`, left, y);
    doc.text(meta.time, right, y, { align: 'right' });
    y += 5;

    // Items header
    doc.setFont('helvetica', 'bold');
    doc.text('Item', left, y);
    doc.text('Qty', left + 33, y, { align: 'right' });
    doc.text('Rate', left + 50, y, { align: 'right' });
    doc.text('Amt', right, y, { align: 'right' });
    y += 3;
    doc.line(left, y, right, y);
    y += 4;
    doc.setFont('helvetica', 'normal');
    items.forEach((it) => {
      const qty = Number(it.qty) || 0;
      const price = Number(it.price) || 0;
      const amt = qty * price;
      const nameLines = doc.splitTextToSize(it.name || '-', 32);
      doc.text(nameLines, left, y);
      doc.text(String(qty), left + 33, y, { align: 'right' });
      doc.text(price.toFixed(2), left + 50, y, { align: 'right' });
      doc.text(amt.toFixed(2), right, y, { align: 'right' });
      y += Math.max(4, nameLines.length * 4);
    });
    y += 2;
    doc.line(left, y, right, y);
    y += 4;

    const row = (label, value, bold = false) => {
      doc.setFont('helvetica', bold ? 'bold' : 'normal');
      doc.setFontSize(bold ? 11 : 9);
      doc.setTextColor(0, 0, 0);
      doc.text(label, left, y);
      doc.text(value, right, y, { align: 'right' });
      y += bold ? 6 : 4.5;
    };
    row('Subtotal', formatINR(totals.subtotal));
    if (totals.discount > 0) row(`Disc ${tax.discountPercent}%`, `- ${formatINR(totals.discount)}`);
    if (totals.serviceCharge > 0) row(`Service ${tax.serviceCharge}%`, formatINR(totals.serviceCharge));
    if (Number(tax.cgst) > 0) row(`CGST ${tax.cgst}%`, formatINR(totals.cgst));
    if (Number(tax.sgst) > 0) row(`SGST ${tax.sgst}%`, formatINR(totals.sgst));
    y += 1;
    doc.setDrawColor(...color);
    doc.line(left, y, right, y);
    y += 5;
    doc.setTextColor(...color);
    row('GRAND TOTAL', `Rs. ${formatINR(totals.grand)}`, true);
    doc.setTextColor(0, 0, 0);
    y += 4;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(`Paid via ${meta.paymentMode}`, (w + stripW) / 2, y, { align: 'center' });
    y += 6;
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...color);
    doc.text('Thank you! Visit again.', (w + stripW) / 2, y, { align: 'center' });
    return doc;
  };

  // 5. A4 GST INVOICE
  const renderInvoice = () => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const w = 210;
    const left = 14;
    const right = w - 14;
    const color = hexToRgbArr(accent);
    let y = 0;

    doc.setFillColor(...color);
    doc.rect(0, 0, w, 32, 'F');
    if (logo) drawPdfLogo(doc, logo, left, 4, 22, restaurant.name, [255, 255, 255]);
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.text(restaurant.name || 'Restaurant', logo ? left + 26 : left, 14);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text('TAX INVOICE', logo ? left + 26 : left, 22);
    if (restaurant.gstin) doc.text(`GSTIN: ${restaurant.gstin}`, logo ? left + 26 : left, 28);
    doc.setFontSize(9);
    doc.text(restaurant.address || '', right, 14, { align: 'right' });
    if (restaurant.phone) doc.text(`Tel: ${restaurant.phone}`, right, 20, { align: 'right' });
    if (restaurant.fssai) doc.text(`FSSAI: ${restaurant.fssai}`, right, 26, { align: 'right' });

    y = 42;
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Billed To', left, y);
    doc.text('Invoice Details', w / 2, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.text(meta.customer || 'Walk-in Guest', left, y);
    doc.text(`Bill No: ${meta.billNo}`, w / 2, y);
    y += 5;
    doc.text(`Table: ${meta.tableNo || '-'}${meta.server ? `   Server: ${meta.server}` : ''}`, left, y);
    doc.text(`Date: ${meta.date}   Time: ${meta.time}`, w / 2, y);
    y += 10;

    // Items table header
    doc.setFillColor(...color);
    doc.rect(left, y, right - left, 9, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('#', left + 3, y + 6);
    doc.text('Item', left + 12, y + 6);
    doc.text('Qty', right - 70, y + 6, { align: 'right' });
    doc.text('Rate', right - 45, y + 6, { align: 'right' });
    doc.text('Amount', right - 3, y + 6, { align: 'right' });
    y += 9;

    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    items.forEach((it, idx) => {
      const qty = Number(it.qty) || 0;
      const price = Number(it.price) || 0;
      const amt = qty * price;
      const nameLines = doc.splitTextToSize(it.name || '-', right - left - 95);
      const rowH = Math.max(7, nameLines.length * 5 + 2);
      doc.rect(left, y, right - left, rowH);
      doc.text(String(idx + 1), left + 3, y + 5);
      doc.text(nameLines, left + 12, y + 5);
      doc.text(String(qty), right - 70, y + 5, { align: 'right' });
      doc.text(price.toFixed(2), right - 45, y + 5, { align: 'right' });
      doc.text(amt.toFixed(2), right - 3, y + 5, { align: 'right' });
      y += rowH;
    });

    y += 4;
    const totalsX = right - 90;
    const row = (label, value, bold = false, big = false) => {
      doc.setFont('helvetica', bold ? 'bold' : 'normal');
      doc.setFontSize(big ? 13 : 10);
      doc.text(label, totalsX, y);
      doc.text(value, right, y, { align: 'right' });
      y += big ? 8 : 5;
    };
    row('Subtotal', formatINR(totals.subtotal));
    if (totals.discount > 0) row(`Discount (${tax.discountPercent}%)`, `- ${formatINR(totals.discount)}`);
    if (totals.serviceCharge > 0) row(`Service (${tax.serviceCharge}%)`, formatINR(totals.serviceCharge));
    if (Number(tax.cgst) > 0) row(`CGST (${tax.cgst}%)`, formatINR(totals.cgst));
    if (Number(tax.sgst) > 0) row(`SGST (${tax.sgst}%)`, formatINR(totals.sgst));
    y += 1;
    doc.setDrawColor(...color);
    doc.setLineWidth(0.4);
    doc.line(totalsX, y, right, y);
    y += 6;
    doc.setTextColor(...color);
    row('GRAND TOTAL', `Rs. ${formatINR(totals.grand)}`, true, true);
    doc.setTextColor(0, 0, 0);

    y += 4;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Payment Mode: ${meta.paymentMode}`, left, y);

    y += 24;
    doc.setDrawColor(120, 120, 120);
    doc.line(right - 60, y, right, y);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('Authorised Signatory', right - 30, y + 6, { align: 'center' });

    doc.setFont('helvetica', 'italic');
    doc.setFontSize(10);
    doc.setTextColor(...color);
    doc.text('Thank you! Please visit us again.', w / 2, 285, { align: 'center' });
    return doc;
  };

  const TEMPLATE_RENDERERS = {
    classic: renderClassic,
    banner: renderBanner,
    minimal: renderMinimal,
    sidebar: renderSidebar,
    invoice: renderInvoice,
  };

  const buildPDF = () => (TEMPLATE_RENDERERS[template] || renderClassic)();

  const handleDownloadPDF = () => {
    const doc = buildPDF();
    doc.save(`Restaurant_Bill_${meta.billNo || 'receipt'}.pdf`);
  };
  const handlePrint = () => {
    const doc = buildPDF();
    doc.autoPrint();
    const url = doc.output('bloburl');
    window.open(url, '_blank');
  };

  return (
    <div className="calculator-page fade-in">
      <SEO
        title="Restaurant Bill Generator"
        description="Free Restaurant Bill Generator India with GST (CGST/SGST), service charge, discount, table number and 5 printable templates. Download bill as PDF instantly."
        keywords="restaurant bill generator, restaurant invoice generator, GST restaurant bill, cafe bill format, hotel bill pdf, restaurant receipt india, FSSAI bill"
        canonicalPath="/restaurant-bill"
        calculatorType="RestaurantBillGenerator"
        calculatorDescription="Generate Indian restaurant / cafe bills with itemised list, GST split, service charge, and 5 printable templates. Download or print as PDF."
      />

      <div className="calculator-header">
        <h1>Restaurant Bill Generator</h1>
        <p>Itemised GST-compliant restaurant bills with 5 printable templates</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          <div className="input-group">
            <label>Bill Template</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
              {TEMPLATES.map((t) => {
                const active = template === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setTemplate(t.id)}
                    style={{
                      padding: '6px 12px',
                      borderRadius: 999,
                      border: `1.5px solid ${active ? t.accent : '#cbd5e1'}`,
                      background: active ? t.accent : '#fff',
                      color: active ? '#fff' : '#0f172a',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        background: t.accent,
                        border: active ? '1px solid #fff' : 'none',
                      }}
                    />
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          <h3 style={{ marginTop: 12, marginBottom: 12 }}>Restaurant Details</h3>
          <LogoUploader logo={logo} setLogo={setLogo} label="Restaurant Logo" accent={accent} />
          <div className="input-group">
            <label>Restaurant Name</label>
            <input
              type="text"
              value={restaurant.name}
              onChange={(e) => setRestaurant({ ...restaurant, name: e.target.value })}
            />
          </div>
          <div className="input-group">
            <label>Address</label>
            <input
              type="text"
              value={restaurant.address}
              onChange={(e) => setRestaurant({ ...restaurant, address: e.target.value })}
            />
          </div>
          <div className="flex-row">
            <div className="input-group" style={{ flex: 1 }}>
              <label>Phone</label>
              <input
                type="text"
                value={restaurant.phone}
                onChange={(e) => setRestaurant({ ...restaurant, phone: e.target.value })}
              />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>GSTIN</label>
              <input
                type="text"
                value={restaurant.gstin}
                onChange={(e) => setRestaurant({ ...restaurant, gstin: e.target.value.toUpperCase() })}
              />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>FSSAI</label>
              <input
                type="text"
                value={restaurant.fssai}
                onChange={(e) => setRestaurant({ ...restaurant, fssai: e.target.value })}
              />
            </div>
          </div>

          <h3 style={{ marginTop: 12, marginBottom: 12 }}>Bill Info</h3>
          <div className="flex-row">
            <div className="input-group" style={{ flex: 1 }}>
              <label>Bill No</label>
              <input
                type="text"
                value={meta.billNo}
                onChange={(e) => setMeta({ ...meta, billNo: e.target.value })}
              />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Date</label>
              <input
                type="text"
                value={meta.date}
                onChange={(e) => setMeta({ ...meta, date: e.target.value })}
              />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Time</label>
              <input
                type="text"
                value={meta.time}
                onChange={(e) => setMeta({ ...meta, time: e.target.value })}
              />
            </div>
          </div>
          <div className="flex-row">
            <div className="input-group" style={{ flex: 1 }}>
              <label>Table No</label>
              <input
                type="text"
                value={meta.tableNo}
                onChange={(e) => setMeta({ ...meta, tableNo: e.target.value })}
              />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Server</label>
              <input
                type="text"
                value={meta.server}
                onChange={(e) => setMeta({ ...meta, server: e.target.value })}
              />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Customer (optional)</label>
              <input
                type="text"
                value={meta.customer}
                onChange={(e) => setMeta({ ...meta, customer: e.target.value })}
              />
            </div>
          </div>

          <h3 style={{ marginTop: 12, marginBottom: 12 }}>Items</h3>
          <div
            style={{
              border: '1px solid var(--border-color, #e2e8f0)',
              borderRadius: 10,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '2fr 70px 90px 90px 40px',
                gap: 6,
                padding: '8px 10px',
                background: 'var(--background-color, #f8fafc)',
                fontSize: 12,
                fontWeight: 700,
                color: '#64748b',
              }}
            >
              <div>Item</div>
              <div style={{ textAlign: 'right' }}>Qty</div>
              <div style={{ textAlign: 'right' }}>Rate (₹)</div>
              <div style={{ textAlign: 'right' }}>Amount</div>
              <div />
            </div>
            {items.map((it) => {
              const amt = (Number(it.qty) || 0) * (Number(it.price) || 0);
              return (
                <div
                  key={it.id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '2fr 70px 90px 90px 40px',
                    gap: 6,
                    padding: '6px 10px',
                    alignItems: 'center',
                    borderTop: '1px solid #e2e8f0',
                  }}
                >
                  <input
                    type="text"
                    value={it.name}
                    onChange={(e) => updateItem(it.id, { name: e.target.value })}
                    placeholder="Item name"
                    style={{ padding: 6, fontSize: 13 }}
                  />
                  <input
                    type="number"
                    value={it.qty}
                    onChange={(e) =>
                      updateItem(it.id, {
                        qty: e.target.value === '' ? '' : Number(e.target.value),
                      })
                    }
                    min="0"
                    step="1"
                    style={{ padding: 6, fontSize: 13, textAlign: 'right' }}
                  />
                  <input
                    type="number"
                    value={it.price}
                    onChange={(e) =>
                      updateItem(it.id, {
                        price: e.target.value === '' ? '' : Number(e.target.value),
                      })
                    }
                    min="0"
                    step="0.01"
                    style={{ padding: 6, fontSize: 13, textAlign: 'right' }}
                  />
                  <div style={{ textAlign: 'right', fontSize: 13, fontWeight: 600 }}>
                    ₹ {formatINR(amt)}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeItem(it.id)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: '#dc2626',
                      cursor: 'pointer',
                    }}
                    aria-label="Remove item"
                  >
                    <FaTrash />
                  </button>
                </div>
              );
            })}
          </div>
          <button
            type="button"
            className="btn btn-outline"
            style={{ marginTop: 8, padding: '6px 12px' }}
            onClick={addItem}
          >
            <FaPlus style={{ marginRight: 6 }} /> Add Item
          </button>

          <h3 style={{ marginTop: 16, marginBottom: 12 }}>Taxes & Charges</h3>
          <div className="flex-row">
            <div className="input-group" style={{ flex: 1 }}>
              <label>CGST (%)</label>
              <input
                type="number"
                value={tax.cgst}
                onChange={(e) =>
                  setTax({ ...tax, cgst: e.target.value === '' ? '' : Number(e.target.value) })
                }
                min="0"
                step="0.01"
              />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>SGST (%)</label>
              <input
                type="number"
                value={tax.sgst}
                onChange={(e) =>
                  setTax({ ...tax, sgst: e.target.value === '' ? '' : Number(e.target.value) })
                }
                min="0"
                step="0.01"
              />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Service Charge (%)</label>
              <input
                type="number"
                value={tax.serviceCharge}
                onChange={(e) =>
                  setTax({
                    ...tax,
                    serviceCharge: e.target.value === '' ? '' : Number(e.target.value),
                  })
                }
                min="0"
                step="0.01"
              />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Discount (%)</label>
              <input
                type="number"
                value={tax.discountPercent}
                onChange={(e) =>
                  setTax({
                    ...tax,
                    discountPercent: e.target.value === '' ? '' : Number(e.target.value),
                  })
                }
                min="0"
                max="100"
                step="0.01"
              />
            </div>
          </div>

          <div className="input-group">
            <label>Payment Mode</label>
            <select
              value={meta.paymentMode}
              onChange={(e) => setMeta({ ...meta, paymentMode: e.target.value })}
            >
              {PAYMENT_MODES.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>

          <div
            style={{
              marginTop: 12,
              padding: '10px 12px',
              borderRadius: 10,
              background: '#f0fdf4',
              border: '1px solid #bbf7d0',
              fontSize: 14,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Subtotal</span>
              <strong>₹ {formatINR(totals.subtotal)}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
              <span>Tax + Service</span>
              <strong>
                ₹ {formatINR(totals.cgst + totals.sgst + totals.serviceCharge)}
              </strong>
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: 6,
                fontSize: 16,
                color: accent,
              }}
            >
              <strong>Grand Total</strong>
              <strong>₹ {formatINR(totals.grand)}</strong>
            </div>
          </div>

          <div className="button-group" style={{ marginTop: 16 }}>
            <button className="btn btn-outline" onClick={handleReset}>
              <FaRedo style={{ marginRight: 8 }} /> Reset
            </button>
            <button className="btn btn-primary" onClick={handleDownloadPDF}>
              <FaDownload style={{ marginRight: 8 }} /> Download PDF
            </button>
            <button className="btn btn-primary" onClick={handlePrint}>
              <FaPrint style={{ marginRight: 8 }} /> Print
            </button>
          </div>
        </div>

        <div className="calculator-results">
          <h3 style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            <FaUtensils /> Bill Preview
          </h3>

          <BillPreview
            template={template}
            accent={accent}
            restaurant={restaurant}
            meta={meta}
            items={items}
            tax={tax}
            totals={totals}
            logo={logo}
          />
        </div>
      </div>
    </div>
  );
};

// ---------- LIVE PREVIEW ----------
const ItemsTable = ({ items, accent, dense = false }) => (
  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: dense ? 11 : 12, marginTop: 8 }}>
    <thead>
      <tr style={{ borderBottom: `1px solid ${accent}`, textAlign: 'left' }}>
        <th style={{ padding: '4px 0' }}>Item</th>
        <th style={{ padding: '4px 0', textAlign: 'right', width: 40 }}>Qty</th>
        <th style={{ padding: '4px 0', textAlign: 'right', width: 60 }}>Rate</th>
        <th style={{ padding: '4px 0', textAlign: 'right', width: 70 }}>Amt</th>
      </tr>
    </thead>
    <tbody>
      {items.map((it) => {
        const qty = Number(it.qty) || 0;
        const price = Number(it.price) || 0;
        return (
          <tr key={it.id} style={{ borderBottom: '1px dashed #e2e8f0' }}>
            <td style={{ padding: '4px 0' }}>{it.name || '—'}</td>
            <td style={{ padding: '4px 0', textAlign: 'right' }}>{qty}</td>
            <td style={{ padding: '4px 0', textAlign: 'right' }}>{price.toFixed(2)}</td>
            <td style={{ padding: '4px 0', textAlign: 'right' }}>{(qty * price).toFixed(2)}</td>
          </tr>
        );
      })}
    </tbody>
  </table>
);

const TotalsBlock = ({ tax, totals, accent, big = false }) => (
  <div style={{ marginTop: 10, fontSize: 12 }}>
    <Row label="Subtotal" value={`₹ ${formatINR(totals.subtotal)}`} />
    {totals.discount > 0 && (
      <Row label={`Discount (${tax.discountPercent}%)`} value={`- ₹ ${formatINR(totals.discount)}`} />
    )}
    {totals.serviceCharge > 0 && (
      <Row label={`Service (${tax.serviceCharge}%)`} value={`₹ ${formatINR(totals.serviceCharge)}`} />
    )}
    {Number(tax.cgst) > 0 && (
      <Row label={`CGST (${tax.cgst}%)`} value={`₹ ${formatINR(totals.cgst)}`} />
    )}
    {Number(tax.sgst) > 0 && (
      <Row label={`SGST (${tax.sgst}%)`} value={`₹ ${formatINR(totals.sgst)}`} />
    )}
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: 8,
        paddingTop: 8,
        borderTop: `2px solid ${accent}`,
        fontSize: big ? 16 : 14,
        fontWeight: 800,
        color: accent,
      }}
    >
      <span>GRAND TOTAL</span>
      <span>₹ {formatINR(totals.grand)}</span>
    </div>
  </div>
);

const Row = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 3 }}>
    <span>{label}</span>
    <span>{value}</span>
  </div>
);

const BillPreview = ({ template, accent, restaurant, meta, items, tax, totals, logo }) => {
  const cardBase = {
    background: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: 12,
    margin: '0 auto',
    boxShadow: '0 2px 12px rgba(15, 23, 42, 0.08)',
    fontFamily: 'Helvetica, Arial, sans-serif',
    color: '#0f172a',
    overflow: 'hidden',
  };

  if (template === 'classic') {
    return (
      <div style={{ ...cardBase, padding: '24px 22px', maxWidth: 360 }}>
        {logo && (
          <div style={{ textAlign: 'center', marginBottom: 6 }}>
            <img src={logo} alt="logo" style={{ height: 48, objectFit: 'contain' }} />
          </div>
        )}
        <div style={{ textAlign: 'center', fontWeight: 800, fontSize: 18, color: accent }}>
          {restaurant.name || 'Restaurant'}
        </div>
        {restaurant.address && (
          <div style={{ textAlign: 'center', fontSize: 11, color: '#64748b', marginTop: 2 }}>
            {restaurant.address}
          </div>
        )}
        {restaurant.phone && (
          <div style={{ textAlign: 'center', fontSize: 11, color: '#64748b' }}>Tel: {restaurant.phone}</div>
        )}
        {restaurant.gstin && (
          <div style={{ textAlign: 'center', fontSize: 11, color: '#64748b' }}>GSTIN: {restaurant.gstin}</div>
        )}
        <div style={{ borderTop: '1px solid #e2e8f0', margin: '10px 0' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
          <span>Bill: <strong>{meta.billNo}</strong></span>
          <span>{meta.date}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginTop: 2 }}>
          <span>Table: <strong>{meta.tableNo || '-'}</strong></span>
          <span>{meta.time}</span>
        </div>
        <ItemsTable items={items} accent={accent} />
        <TotalsBlock tax={tax} totals={totals} accent={accent} />
        <div style={{ textAlign: 'center', fontSize: 11, color: '#64748b', marginTop: 8 }}>
          Paid via {meta.paymentMode}
        </div>
        <div style={{ textAlign: 'center', fontWeight: 700, color: accent, marginTop: 6 }}>
          Thank you! Visit again.
        </div>
      </div>
    );
  }

  if (template === 'banner') {
    return (
      <div style={{ ...cardBase, maxWidth: 380 }}>
        <div style={{ background: accent, color: '#fff', padding: '14px 16px', textAlign: 'center' }}>
          {logo && (
            <div style={{ marginBottom: 6 }}>
              <img src={logo} alt="logo" style={{ height: 44, background: '#fff', borderRadius: 6, padding: 4, objectFit: 'contain' }} />
            </div>
          )}
          <div style={{ fontWeight: 800, fontSize: 18 }}>{restaurant.name || 'Restaurant'}</div>
          <div style={{ fontSize: 11, opacity: 0.9, marginTop: 2 }}>TAX INVOICE</div>
          {restaurant.gstin && <div style={{ fontSize: 10, opacity: 0.85 }}>GSTIN {restaurant.gstin}</div>}
        </div>
        <div style={{ padding: 16 }}>
          {restaurant.address && <div style={{ textAlign: 'center', fontSize: 11, color: '#64748b' }}>{restaurant.address}</div>}
          {restaurant.phone && <div style={{ textAlign: 'center', fontSize: 11, color: '#64748b' }}>Tel: {restaurant.phone}</div>}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginTop: 10 }}>
            <span>Bill: <strong>{meta.billNo}</strong></span>
            <span>{meta.date} {meta.time}</span>
          </div>
          <div style={{ fontSize: 12 }}>
            Table {meta.tableNo || '-'}{meta.server && ` | ${meta.server}`}
          </div>
          <ItemsTable items={items} accent={accent} />
          <TotalsBlock tax={tax} totals={totals} accent={accent} />
          <div
            style={{
              marginTop: 10,
              background: accent,
              color: '#fff',
              padding: '10px 14px',
              borderRadius: 6,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span style={{ fontWeight: 700, fontSize: 14 }}>TOTAL DUE</span>
            <span style={{ fontWeight: 800, fontSize: 18 }}>₹ {formatINR(totals.grand)}</span>
          </div>
          <div style={{ textAlign: 'center', fontSize: 11, color: '#64748b', marginTop: 8 }}>
            Paid via {meta.paymentMode}
          </div>
          <div style={{ textAlign: 'center', fontWeight: 700, color: accent, marginTop: 4 }}>
            Thank you! Visit again.
          </div>
        </div>
      </div>
    );
  }

  if (template === 'minimal') {
    return (
      <div style={{ ...cardBase, padding: 24, maxWidth: 360, boxShadow: 'none', border: '1px solid #e5e7eb' }}>
        {logo && (
          <div style={{ textAlign: 'center', marginBottom: 6 }}>
            <img src={logo} alt="logo" style={{ height: 42, objectFit: 'contain' }} />
          </div>
        )}
        <div style={{ textAlign: 'center', fontWeight: 800, fontSize: 18 }}>
          {restaurant.name || 'Restaurant'}
        </div>
        {restaurant.address && (
          <div style={{ textAlign: 'center', fontSize: 11, color: '#64748b', marginTop: 2 }}>{restaurant.address}</div>
        )}
        {restaurant.phone && (
          <div style={{ textAlign: 'center', fontSize: 11, color: '#64748b' }}>{restaurant.phone}</div>
        )}
        <div style={{ borderTop: '1.5px solid #111827', margin: '10px 0' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
          <span>{meta.billNo}</span>
          <span>{meta.date} {meta.time}</span>
        </div>
        <div style={{ fontSize: 11, color: '#64748b' }}>
          Table {meta.tableNo || '-'}{meta.server && ` · ${meta.server}`}
        </div>
        <ItemsTable items={items} accent="#111827" dense />
        <TotalsBlock tax={tax} totals={totals} accent="#111827" />
        <div style={{ textAlign: 'center', fontSize: 11, fontStyle: 'italic', color: '#64748b', marginTop: 10 }}>
          Paid via {meta.paymentMode} · Thank you. Please visit again.
        </div>
      </div>
    );
  }

  if (template === 'sidebar') {
    return (
      <div style={{ ...cardBase, display: 'flex', maxWidth: 400 }}>
        <div style={{ background: accent, width: 14, flexShrink: 0 }} />
        <div style={{ padding: 18, flex: 1 }}>
          {logo && (
            <div style={{ textAlign: 'center', marginBottom: 6 }}>
              <img src={logo} alt="logo" style={{ height: 46, objectFit: 'contain' }} />
            </div>
          )}
          <div style={{ textAlign: 'center', fontWeight: 800, fontSize: 18 }}>{restaurant.name || 'Restaurant'}</div>
          {restaurant.address && (
            <div style={{ textAlign: 'center', fontSize: 11, color: '#64748b', marginTop: 2 }}>{restaurant.address}</div>
          )}
          {restaurant.phone && (
            <div style={{ textAlign: 'center', fontSize: 11, color: '#64748b' }}>Tel: {restaurant.phone}</div>
          )}
          {restaurant.gstin && (
            <div style={{ textAlign: 'center', fontSize: 11, color: '#64748b' }}>GSTIN {restaurant.gstin}</div>
          )}
          <div style={{ borderTop: `1px solid ${accent}`, margin: '10px 0', opacity: 0.5 }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
            <span>Bill: <strong>{meta.billNo}</strong></span>
            <span>{meta.date} {meta.time}</span>
          </div>
          <div style={{ fontSize: 12 }}>
            Table {meta.tableNo || '-'}{meta.server && ` | ${meta.server}`}
          </div>
          <ItemsTable items={items} accent={accent} />
          <TotalsBlock tax={tax} totals={totals} accent={accent} />
          <div style={{ textAlign: 'center', fontSize: 11, color: '#64748b', marginTop: 8 }}>
            Paid via {meta.paymentMode}
          </div>
          <div style={{ textAlign: 'center', fontWeight: 700, color: accent, marginTop: 4 }}>
            Thank you! Visit again.
          </div>
        </div>
      </div>
    );
  }

  if (template === 'invoice') {
    return (
      <div style={{ ...cardBase, maxWidth: 560 }}>
        <div style={{ background: accent, color: '#fff', padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            {logo && (
              <img src={logo} alt="logo" style={{ width: 56, height: 56, background: '#fff', borderRadius: 6, padding: 4, objectFit: 'contain', flexShrink: 0 }} />
            )}
            <div>
              <div style={{ fontWeight: 800, fontSize: 20 }}>{restaurant.name || 'Restaurant'}</div>
              <div style={{ fontSize: 11, opacity: 0.9 }}>TAX INVOICE</div>
              {restaurant.gstin && <div style={{ fontSize: 11, opacity: 0.9 }}>GSTIN: {restaurant.gstin}</div>}
            </div>
          </div>
          <div style={{ textAlign: 'right', fontSize: 11, opacity: 0.95 }}>
            {restaurant.address && <div>{restaurant.address}</div>}
            {restaurant.phone && <div>Tel: {restaurant.phone}</div>}
            {restaurant.fssai && <div>FSSAI: {restaurant.fssai}</div>}
          </div>
        </div>
        <div style={{ padding: 18 }}>
          <div style={{ display: 'flex', gap: 18, fontSize: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Billed To</div>
              <div>{meta.customer || 'Walk-in Guest'}</div>
              <div style={{ color: '#64748b', marginTop: 2 }}>Table: {meta.tableNo || '-'}{meta.server && ` · Server: ${meta.server}`}</div>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Invoice Details</div>
              <div>Bill No: {meta.billNo}</div>
              <div>Date: {meta.date}  Time: {meta.time}</div>
            </div>
          </div>
          <table style={{ width: '100%', marginTop: 14, borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: accent, color: '#fff' }}>
                <th style={{ padding: 6, textAlign: 'left', width: 30 }}>#</th>
                <th style={{ padding: 6, textAlign: 'left' }}>Item</th>
                <th style={{ padding: 6, textAlign: 'right', width: 50 }}>Qty</th>
                <th style={{ padding: 6, textAlign: 'right', width: 70 }}>Rate</th>
                <th style={{ padding: 6, textAlign: 'right', width: 80 }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => {
                const qty = Number(it.qty) || 0;
                const price = Number(it.price) || 0;
                return (
                  <tr key={it.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: 6 }}>{idx + 1}</td>
                    <td style={{ padding: 6 }}>{it.name || '—'}</td>
                    <td style={{ padding: 6, textAlign: 'right' }}>{qty}</td>
                    <td style={{ padding: 6, textAlign: 'right' }}>{price.toFixed(2)}</td>
                    <td style={{ padding: 6, textAlign: 'right' }}>{(qty * price).toFixed(2)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
            <div style={{ width: 220 }}>
              <TotalsBlock tax={tax} totals={totals} accent={accent} big />
            </div>
          </div>
          <div style={{ marginTop: 8, fontSize: 12 }}>
            Payment Mode: <strong>{meta.paymentMode}</strong>
          </div>
          <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ borderTop: '1px solid #94a3b8', width: 180, paddingTop: 6, fontWeight: 700, fontSize: 11 }}>
                Authorised Signatory
              </div>
            </div>
          </div>
          <div style={{ textAlign: 'center', fontStyle: 'italic', color: accent, marginTop: 10, fontSize: 11 }}>
            Thank you! Please visit us again.
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default RestaurantBillGenerator;
